/**
 * Inventory cube-sheet math (Phase 8e).
 *
 * Inventory converts a list of items into total CuFT and total weight, which
 * inform the broker's judgment. It does NOT appear on any customer-facing
 * document — the only path that writes a lead's weight/cuft from inventory is
 * the explicit "Apply totals to lead" action (see routes/inventory.ts).
 *
 * Conversion (V9 §1):
 *   itemCuft   = custom_cuft   ?? catalog.cuft                  ?? 0
 *   density    = custom_density ?? catalog.density_lbs_per_cuft ?? 7   (lbs/CuFT)
 *   qty        = quantity || 0
 *   totalCuft  += itemCuft * qty
 *   totalWeight += itemCuft * density * qty
 *
 * Dense items (e.g. a box of books) carry their own density (e.g. 25) — we
 * always use the item's density, never a blanket 7.
 *
 * This helper is pure and mirrored server-side in backend/src/lib/inventory.ts.
 * Keep the two in sync.
 */

export const DEFAULT_DENSITY = 7;

export interface InventoryItemRow {
  catalog_item_id?: string | null;
  custom_cuft?: number | null;
  custom_density?: number | null;
  quantity?: number | null;
}

export interface CatalogItem {
  id: string;
  cuft?: number | null;
  density_lbs_per_cuft?: number | null;
}

export interface InventoryTotals {
  totalCuft: number;
  totalWeight: number;
}

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Effective CuFT for one item (custom override wins over the catalog value). */
export function itemCuft(item: InventoryItemRow, catalog?: CatalogItem | null): number {
  if (item.custom_cuft != null) return num(item.custom_cuft);
  return num(catalog?.cuft);
}

/** Effective density for one item (custom override, then catalog, then default). */
export function itemDensity(item: InventoryItemRow, catalog?: CatalogItem | null): number {
  if (item.custom_density != null) return num(item.custom_density);
  if (catalog?.density_lbs_per_cuft != null) return num(catalog.density_lbs_per_cuft);
  return DEFAULT_DENSITY;
}

/** Line weight = cuft * density * qty. */
export function itemWeight(item: InventoryItemRow, catalog?: CatalogItem | null): number {
  const qty = num(item.quantity);
  return itemCuft(item, catalog) * itemDensity(item, catalog) * qty;
}

/**
 * Sum a list of lead_inventory_items into { totalCuft, totalWeight }.
 * `catalogById` maps catalog_item_id -> catalog row; custom items pass their
 * own cuft/density so a missing catalog entry is fine.
 */
export function computeInventoryTotals(
  items: InventoryItemRow[],
  catalogById: Record<string, CatalogItem>
): InventoryTotals {
  let totalCuft = 0;
  let totalWeight = 0;
  for (const item of items ?? []) {
    const catalog = item.catalog_item_id ? catalogById[item.catalog_item_id] : null;
    const qty = num(item.quantity);
    const cuft = itemCuft(item, catalog);
    const density = itemDensity(item, catalog);
    totalCuft += cuft * qty;
    totalWeight += cuft * density * qty;
  }
  return { totalCuft, totalWeight };
}
